import { Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBootstrapJobDownloadUrls } from '@/hooks/use-bootstrap-jobs';
import { formatFileSize, formatDateTime } from '@/lib/utils/format';

interface DownloadDialogProps {
  jobId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DownloadDialog({ jobId, open, onOpenChange }: DownloadDialogProps) {
  const { data, isLoading } = useBootstrapJobDownloadUrls(open ? jobId : null);

  const downloadFile = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadAll = async () => {
    if (!data?.files) return;

    for (const file of data.files) {
      downloadFile(file.presigned_url);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Download Files</DialogTitle>
          <DialogDescription>
            Download the exported ITSM data files for this job.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-80 overflow-y-auto">
          {isLoading ? (
            <>
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </>
          ) : data?.files && data.files.length > 0 ? (
            data.files.map((file) => {
              const filename = file.key.split('/').pop() ?? file.key;
              return (
                <div
                  key={file.key}
                  className="flex items-center justify-between rounded-md border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size_bytes)} &middot; {formatDateTime(file.last_modified)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-2 shrink-0"
                    onClick={() => { downloadFile(file.presigned_url); }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No files available for download.
            </p>
          )}
        </div>

        {data?.files && data.files.length > 1 && (
          <div className="flex justify-end pt-2">
            <Button onClick={() => { void handleDownloadAll(); }}>
              <Download className="h-4 w-4 mr-2" />
              Download All
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
